import type { PlayerAccountClient, UseQueryOptions } from '../../types'
import { useOpenapi, useMutation, useQuery, useCookie, useCoreConfig, computed } from '#imports'

/**
 * @title usePlayerAccountClient
 * @see Docs https://player-account-prod.spilnu.dk/api/documentation
 */
export const usePlayerAccountClient = () => {
    const { site } = useCoreConfig()
    const client = useOpenapi<PlayerAccountClient.paths>({
        baseUrl: '/gateway/player-account/api',
        service: 'playerAccount'
    })

    const session_expiry = useCookie('session_expiry', {
        readonly: true
    })

    function useSetPhoneNumber() {
        return useMutation(async (body: PlayerAccountClient.components['schemas']['SetPhoneNumberRequest']) => {
            const { data } = await client.PUT('/public/user/set-phone-number', {
                body
            })

            return data
        })
    }

    function useSetPhoneNumberAndEmail() {
        return useMutation(async (body: PlayerAccountClient.components['schemas']['SetPhoneNumberAndEmailRequest']) => {
            const { data } = await client.PUT('/public/user/set-phone-number-and-email', {
                body: {
                    ...body,
                    brand: site.brand as PlayerAccountClient.Brand
                }
            })

            return data
        })
    }

    /**
     * User info query. For a full-featured version with isLoggedIn checks and more use useUserInfo @see {@link https://gitlab.cego.dk/spilnu/npm-packages/core/-/blob/mk/migrate-to-user-info/src/runtime/composables/data/useUserInfo.ts#L3}
     */
    const useGetUserInfo = (options: Pick<UseQueryOptions<any>, 'enabled' | 'lazy' | 'server'> = {}) => {
        const { enabled = computed(() => !!session_expiry.value), lazy = true, server = false } = options

        const query = useQuery(async () => {
            const { data } = await client.GET('/public/user/info', {
                params: { query: { brand: site.brand } },
            })

            return data
        }, {
            key: 'playerAccount:userInfo',
            server,
            lazy,
            enabled
        })

        return query
    }

    function useGetOccupationTypes() {
        return useQuery(async () => {
            const { data } = await client.GET('/public/occupation/types')

            return data
        }, {
            key: 'playerAccount:occupation:types',
            server: false,
            lazy: true
        })
    }

    function useUpdateOccupation() {
        return useMutation(async (body: NonNullable<PlayerAccountClient.paths['/public/user/occupation']['post']['requestBody']>['content']['application/json']) => {
            const { data } = await client.POST('/public/user/occupation', {
                body
            })

            return data
        })
    }

    function useGetSalaryTypes() {
        return useQuery(async () => {
            const { data } = await client.GET('/public/salary/types')

            return data
        }, {
            key: 'playerAccount:salary:types',
            server: false,
            lazy: true
        })
    }

    function useUpdateUserSalary() {
        return useMutation(async (body: NonNullable<PlayerAccountClient.paths['/public/user/salary']['post']['requestBody']>['content']['application/json']) => {
            const { data } = await client.POST('/public/user/salary', {
                body
            })

            return data
        })
    }

    function useGetPartnerData() {
        return useQuery(async () => {
            const { data } = await client.GET('/public/user/partner-data')

            return data
        }, {
            key: 'playerAccount:get-partner-data',
            server: false,
            lazy: false,
            enabled: computed(() => !!session_expiry.value)
        })
    }

    function useSendUserReport() {
        return useMutation(async () => {
            const { data } = await client.POST('/public/user/user-report', {
                body: {
                    brand: site.brand as PlayerAccountClient.Brand
                }
            })

            return data
        })
    }

    function useGetRealityCheckSetting(options: Pick<UseQueryOptions<any>, 'enabled' | 'lazy' | 'server'> = {}) {
        const { enabled = computed(() => !!session_expiry.value), lazy = true, server = false } = options

        return useQuery(async () => {
            const { data } = await client.GET('/public/user/reality-check-setting')

            return data
        }, {
            key: 'playerAccount:realityCheck',
            enabled,
            server,
            lazy
        })
    }

    function useUpdateRealityCheckSetting() {
        return useMutation(async (body: NonNullable<PlayerAccountClient.components['schemas']['RealityCheckSettingData']>) => {
            const { data } = await client.POST('/public/user/reality-check-setting', {
                body
            })

            return data
        })
    }

    function useDeleteRealityCheckSetting(options: Pick<UseQueryOptions<any>, 'enabled' | 'lazy' | 'server'> = {}) {
        const { enabled = !!session_expiry.value, lazy = true, server = false, ...restOptions } = options

        return useMutation(async () => {
            const { data } = await client.DELETE('/public/user/reality-check-setting')

            return data
        }, {
            key: 'playerAccount:realityCheck:delete',
            enabled,
            server,
            lazy,
            ...restOptions
        })
    }

    function useResolvePlayerId() {
        const mutation = useMutation(async (body: PlayerAccountClient.components['schemas']['ResolvePlayerIdRequest']) => {
            const { data } = await client.POST('/public/player-id/resolve', {
                body
            })

            return data as PlayerAccountClient.components['schemas']['ResolvePlayerIdResponse']
        })

        const playerId = computed(() => mutation.data.value?.playerId ?? null)

        async function resolve(identifier: { ssn: string } | { email: string }) {
            if (mutation.pending.value) return
            mutation.reset()
            await mutation.mutateAsync(identifier)
        }

        return { playerId, resolve }
    }

    return {
        client,
        session_expiry,
        useGetUserInfo,
        useSetPhoneNumber,
        useSetPhoneNumberAndEmail,
        useGetOccupationTypes,
        useUpdateOccupation,
        useGetRealityCheckSetting,
        useUpdateRealityCheckSetting,
        useDeleteRealityCheckSetting,
        useGetSalaryTypes,
        useUpdateUserSalary,
        useGetPartnerData,
        useSendUserReport,
        useResolvePlayerId
    }
}
